import { LitElement, html } from 'lit';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import { icon } from '../../icons';
import { choiceStyles } from '../../../../components/form-controls/choice-styles';
import '../cta-button';
import { styles } from './css';

import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';
import { isMobileLayout } from '../../../../helpers/layout';

let __multiSelectUid = 0;
const nextUid = () => `multi-select-${++__multiSelectUid}`;

class StyledMultiSelect extends LitElement {
  static get properties() {
    return {
      _expanded: { type: Boolean },
      _dataReady: { type: Boolean },
      _optionsData: { type: Array },
      _dataHeaderString: { type: String },
      _includeSearch: { type: Boolean },
      _searchString: { type: String },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      styles,
      choiceStyles,
    ];
  }

  constructor() {
    super();

    this._uid = nextUid();
    this._expanded = false;
    this._dataReady = false;
    this._optionsData = [];
    this._dataHeaderString = '';
    this._includeSearch = false;
    this._searchString = '';
    document.addEventListener('clearFilterSelections', e => {
      this.clearFilters();
      this._expanded = false;
    });
    document.addEventListener('visualizedMaterialSelected', e => {
      this._expanded = false;
      this.requestUpdate();
    });
  }

  async connectedCallback() {
    super.connectedCallback();
    this.processOptionsData();

    this._includeSearch = this.includeSearch;
    this._dataHeaderString = this.dataHeaderString;
  }

  disconnectedCallback() {
    document.removeEventListener('clearFilterSelections', e => {
      this.clearFilters();
    });
    super.disconnectedCallback();
  }

  get _headerId() {
    return `${this._uid}-header`;
  }

  get _panelId() {
    return `${this._uid}-panel`;
  }

  get _searchId() {
    return `${this._uid}-search`;
  }

  render() {
    return this._dataReady
      ? html`
          <div class="multi-select-wrapper">
            <button
              class="header"
              type="button"
              aria-expanded=${this._expanded ? 'true' : 'false'}
              aria-controls=${this._panelId}
              @click="${this.toggleVisbility}"
            >
              <p id=${this._headerId}>${this._dataHeaderString}</p>
              ${icon('arrow', this._selectArrowClasses)}
            </button>
            ${this._expanded
              ? html`
                  <div
                    id=${this._panelId}
                    role="group"
                    aria-labelledby=${this._headerId}
                  >
                    ${this._includeSearch ? this.searchHTML : html``}
                    <div class="options-content">
                      ${this._optionsData.length
                        ? this._optionsData.map(
                            (side, sideIndex) => html`
                              <div
                                class="side"
                                role="group"
                                aria-label=${side.humanizedCategory}
                              >
                                <span class="option-category-header">
                                  ${side.humanizedCategory}
                                </span>
                                ${side.options.map((option, optIndex) => {
                                  const inputId = `${this._uid}-opt-${sideIndex}-${optIndex}`;
                                  return html`
                                    <div class="filter-option">
                                      <label
                                        class="tf-choice option-label"
                                        for=${inputId}
                                      >
                                        <span class="tf-choice__control">
                                          <input
                                            type="checkbox"
                                            id=${inputId}
                                            .checked=${option.selected}
                                            @change="${() => {
                                              option.selected = !option.selected;
                                              this.patternFilterSelected(
                                                side,
                                                option
                                              );
                                            }}"
                                          />
                                          <span
                                            class="tf-choice__control-fill"
                                          ></span>
                                        </span>
                                        <span class="tf-choice__text">
                                          ${option.humanizedOption}
                                        </span>
                                      </label>
                                    </div>
                                  `;
                                })}
                              </div>
                            `
                          )
                        : html``}
                    </div>
                    <div class="options-buttons">
                      <cta-button
                        cta-type="secondary"
                        cta-data=${JSON.stringify({
                          url: '',
                          text: 'Clear All',
                          fullWidth: true,
                        })}
                        @click="${() => {
                          this.clearFilters(true);
                          this._expanded = false;
                        }}"
                      ></cta-button>
                      <cta-button
                        cta-type="primary"
                        cta-data=${JSON.stringify({
                          url: '',
                          text: 'Apply',
                          fullWidth: true,
                        })}
                        @click="${() => (this._expanded = false)}"
                      ></cta-button>
                    </div>
                  </div>
                `
              : html``}
          </div>
        `
      : html``;
  }

  get searchHTML() {
    return html`
      <div class="input-wrapper search">
        <label for=${this._searchId} class="visually-hidden">
          Search ${this._dataHeaderString}
        </label>
        <input
          type="text"
          id=${this._searchId}
          placeholder="Search by Name"
          class="search-input"
          .value=${this._searchString}
          @keyup=${e => {
            if (e.key === 'Enter' || e.keyCode === 13) {
              this._expanded = false;
            } else {
              this.updateSearchSelection(e.target.value);
            }
          }}
        />
        ${icon('search', 'search-icon')}
      </div>
    `;
  }

  get _selectArrowClasses() {
    return `select-arrow ${this._expanded ? 'arrow-expanded' : 'arrow-closed'}`;
  }

  updateSearchSelection(inputValue) {
    if (inputValue === this._searchString) return;
    if (inputValue.length !== 1) {
      this.clearFilters();
      this._searchString = inputValue;
      const searchFromFilterEvent = new CustomEvent('searchFromFilterUpdated', {
        detail: {
          inputValue,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(searchFromFilterEvent);
    }
  }

  get optionsDataFromParent() {
    return this.getAttribute('options-data')
      ? JSON.parse(this.getAttribute('options-data'))
      : '';
  }

  get dataHeaderString() {
    return isMobileLayout() && this.includeSearch
      ? 'Search and Filter'
      : 'Filter by Color and Pattern';
  }

  get includeSearch() {
    this._includeSearch = !!this.getAttribute('data-include-search');
    return this._includeSearch;
  }

  processOptionsData() {
    if (!!this.optionsDataFromParent) {
      this.optionsDataFromParent.forEach(optionCategory => {
        if (optionCategory.options) {
          optionCategory.options.forEach(option => {
            option.selected = false;
          });
        }
        if (optionCategory.humanizedCategory === 'Color') {
          this._optionsData.splice(0, 0, optionCategory);
        } else if (optionCategory.humanizedCategory === 'Pattern') {
          this._optionsData.splice(1, 0, optionCategory);
        }
      });
      this._dataReady = true;
      this.requestUpdate();
    }
  }

  patternFilterSelected(category, option) {
    const selectedOptions = [];
    if (this._includeSearch) {
      this._searchString = '';
      this.updateSearchSelection('');
      this.requestUpdate();
    }
    this._optionsData.forEach(grouping => {
      const selectedOptionGroup = [];
      grouping.options.forEach(option => {
        if (option.selected) {
          selectedOptionGroup.push({
            id: option.value,
            category: grouping,
          });
        }
      });
      if (selectedOptionGroup.length > 0)
        selectedOptions.push(selectedOptionGroup);
    });

    let selectedFiltersString = '';
    if (selectedOptions.length > 0) {
      selectedFiltersString = '&tag_names=';
      selectedOptions.forEach(optionGroup => {
        if (selectedFiltersString !== '&tag_names=') {
          selectedFiltersString += ',';
        }
        const selectedOptionIds = optionGroup.map(option => option.id);
        const optionDelimiter = optionGroup[0].category?.inclusive ? '|' : ',';
        selectedFiltersString += selectedOptionIds.join(optionDelimiter);
      });
    }

    const patternFilterUpdatedEvent = new CustomEvent('patternFilterUpdated', {
      detail: {
        selectedFiltersString,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(patternFilterUpdatedEvent);

    if (!!category && !!option) {
      const clickAction = option.selected ? 'Select' : 'DeSelect';
      let analyticsEventString = `Visualization|Filter ${category.humanizedCategory}|${clickAction} ${option.humanizedOption}`;
      this.sendAdobeAnalyticsEvent(analyticsEventString);
    }
  }

  clearFilters(userInvoked = false) {
    this._optionsData.forEach(side => {
      side.options.forEach(option => (option.selected = false));
    });
    this.patternFilterSelected(null, null);
    if (userInvoked) {
      this.sendAdobeAnalyticsEvent('Visualization|Filter|DeSelect All');
    }
  }

  toggleVisbility() {
    this._expanded = !this._expanded;
  }

  sendAdobeAnalyticsEvent(eventStr) {
    const eventData = {
      type: 'tool',
      detail: {
        eVar35: eventStr,
      },
    };

    fireAdobeAnalyticsEvent(eventData);
  }
}
customElements.define('styled-multi-select', StyledMultiSelect);
